import React, { useState } from 'react';
import {
  Button,
  Form,
  Header,
  SpaceBetween,
  Container,
  FormField,
  Input,
  Alert,
  Box,
  Grid
} from '@cloudscape-design/components';
import { submissionsApi } from 'api/Submission';
import { useAuth } from './AuthContext';
import logo from './logo.png';

export function SignInHeader() {
  return (
    <Header
      variant="h1"
      description="Sign in to access your account and manage your submissions."
    >
      Sign In
    </Header>
  );
}

function SignInActions({ onSubmitClick, isLoading }) {
  return (
    <Button
      variant="primary"
      onClick={onSubmitClick}
      loading={isLoading}
      data-testid="signin-button"
    >
      Sign In
    </Button>
  );
}

export default function EnhancedSignInPage() {
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignIn = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrorText(null);

    try {
      const response = await submissionsApi.authenticate({
        email: formData.email,
        password: formData.password
      });

      login(response);
      window.location.href = '/my-submissions';
    } catch (error) {
      setErrorText(error.message || 'Invalid credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSignIn();
  };

  return (
    <div style={{
      height: '60vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#ffffff'
    }}>
      <Box
        padding="l"
        margin="xxl"
        variant="awsui-key-rotation"
        style={{
          width: '80%',
          maxWidth: '1000px',
          minHeight: '500px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          backgroundColor: 'white',
          overflow: 'hidden'
        }}
      >
        <Grid
          gridDefinition={[
            { colspan: { default: 12, xxs: 12, xs: 6 } },
            { colspan: { default: 12, xxs: 12, xs: 6 } }
          ]}
        >
          {/* Left Side - Sign In Form */}
          <div style={{
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%'
          }}>
            <form onSubmit={handleSubmit}>
              <Form
                header={<SignInHeader />}
                actions={
                  <SignInActions
                    onSubmitClick={handleSubmit}
                    isLoading={isLoading}
                  />
                }
              >
                <Container>
                  <SpaceBetween size="l">
                    {errorText && (
                      <Alert
                        type="error"
                        dismissible
                        onDismiss={() => setErrorText(null)}
                      >
                        {errorText}
                      </Alert>
                    )}

                    <FormField
                      label="Email"
                      errorText={errors.email}
                    >
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={({ detail }) =>
                          setFormData(prev => ({ ...prev, email: detail.value }))
                        }
                        placeholder="Enter your email"
                        data-testid="email-input"
                      />
                    </FormField>

                    <FormField
                      label="Password"
                      errorText={errors.password}
                    >
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={({ detail }) =>
                          setFormData(prev => ({ ...prev, password: detail.value }))
                        }
                        placeholder="Enter your password"
                        data-testid="password-input"
                      />
                    </FormField>
                  </SpaceBetween>
                </Container>
              </Form>
            </form>
          </div>

          <div style={{
            backgroundColor: '#1C4679',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '2rem',
            height: '100%',
            borderRadius: '0 8px 8px 0'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
              <div style={{
                width: '160px',
                height: '160px',
                background: 'rgba(255, 255, 255)',
                borderRadius: '12px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '1.5rem',
                padding: '15px',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
              }}>
                <img src={logo} alt="Company Logo" style={{ width: '100%' }} />
              </div>

              <p style={{
                marginTop: '1rem',
                textAlign: 'center',
                maxWidth: '80%',
                color: 'white',
                fontSize: '1.1rem'
              }}>
                Sign in to access your Eduscribe account
              </p>
            </div>
          </div>
        </Grid>
      </Box>
    </div>
  );
}